import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { convertStringToCandidateEmailType } from "../../CandidateProfile/Components/CandidateAccordions/State/useEditEmailState";
import { convertStringToCandidatePhoneNumberType } from "../../CandidateProfile/Components/CandidateAccordions/State/useEditPhoneState";
import { getDateUtc } from "../../MessagingCenter/Utils";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { useCandidatesFilterStore } from "../State/Stores/CandidatesPaginationStore";
import { IFormQueryResponse, IPaginationRequest } from "../Typings/Campaigns";
import {
  IAddCandidate,
  IBulkUpsertCandidateTags,
  ICandidate,
  ICandidateAttachment,
  ICandidateAttachmentsExportRequest,
  ICandidateEmailAddress,
  ICandidateExportRequest,
  ICandidatePhoneNumber,
  IDeleteCandidatesRequest,
  IDeleteCandidatesResult,
  IEditCandidateProfile,
  IEditCandidateProfileRequest,
  IEditCandidateProfileResponse,
  IRollbackCandidateRequest,
} from "../Typings/Candidate";
import {
  IAddCandidateToGroupsCommand,
  ICandidateHistory,
  ICandidateProfile,
  ICandidateProfileGroup,
} from "../Typings/CandidateProfile";
import { IGroup } from "../Typings/Groups";
import { IPagination } from "../Typings/MessageText";
import { SuccessResponse } from "../Typings/Queries";
import { ISuccessResponse } from "../Typings/Response";
import { IUploadCandidateAttachment, IUploadMediaDto } from "../Typings/Uploads";
import { STORAGE_VERSION_DATE } from "../Utilities/browserStorage";
import {
  apiPathCandidateProfile,
  apiPathCandidates,
  apiPathGroup,
  fetcher,
  secureFetcher,
} from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";

// See React Query v4 docs for more info on useQuery
// https://tanstack.com/query/v4/docs/quick-start

/**
 * Gets all candidates for the currently selected client
 * @returns UseQueryResult<ICandidate[]>
 */
export function useCandidatesQuery(disableRefetch?: boolean | undefined) {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useQuery<IPagination<ICandidate>>(
    ["candidates", clientId],
    async () => {
      const response = (await secureFetcher(apiPathCandidates + clientId, token)) as IPagination<ICandidate>;
      response.items = response.items.map((candidate: ICandidate) => parseCandidate(candidate));
      return response;
    },
    {
      enabled: (!!clientId || (disableRefetch ?? false)) && !!token,
    },
  );
}

/**
 * Provides a way to select a subset of the Candidates for the currently selected client
 * @param select - Transform or select a part of the data returned from the original query
 * @param key - The key to use for the new query
 * @typeParam T - The type of the data returned from your select function
 * @returns UseQueryResult<T>
 */
export function useCandidatesQuerySelector<T>(select: (data: ICandidate[]) => T | undefined) {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useQuery<ICandidate[], unknown, T | undefined>(
    ["candidates", clientId],
    async () => {
      const response = (await secureFetcher(apiPathCandidates + clientId, token)) as IPagination<ICandidate>;
      return response.items.map((candidate: ICandidate) => parseCandidate(candidate));
    },
    {
      select,
      enabled: !!clientId && !!token,
    },
  );
}

/**
 * Gets candidates for the currently selected client based on filter criteria
 * @param filterQueryString The query string to filter the candidates by
 * @returns UseQueryResult<IPagination<ICandidate>>
 */
export function useFilteredCandidatesQuery(filterQueryString: string, enabled = true, clientId?: string) {
  const { clientId: selectedClientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();
  const queryId = clientId && clientId.length ? clientId : selectedClientId;

  let fetcherPath = apiPathCandidates + queryId;
  const queryKey = ["candidates", queryId];
  if (filterQueryString) {
    queryKey.push(filterQueryString);
    fetcherPath += filterQueryString;
  }

  return useQuery<IPagination<ICandidate>>(
    queryKey,
    async () => {
      const response = (await secureFetcher(fetcherPath, token)) as IPagination<ICandidate>;
      response.items = response.items.map((candidate: ICandidate) => parseCandidate(candidate));
      return response;
    },
    {
      enabled: enabled && !!queryId && !!filterQueryString && !!token,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
}

export function useFilteredDeleteCandidatesPagingQuery(clientId?: string) {
  const key =
    clientId && clientId.length
      ? `delete-candidates-${clientId}-${STORAGE_VERSION_DATE}`
      : `delete-candidates-${STORAGE_VERSION_DATE}`;
  const { page, filterQueryString, page2FilterQueryString } = useCandidatesFilterStore(key);
  const { data: filteredCandidateList, isLoading: filteredCandidateListLoading } = useFilteredCandidatesQuery(
    filterQueryString,
    true,
    clientId,
  );
  const shouldFetchNextPage = filteredCandidateList && filteredCandidateList.totalPages > page + 1;
  useFilteredCandidatesQuery(page2FilterQueryString ?? "", shouldFetchNextPage, clientId);
  return {
    filteredCandidateList: filteredCandidateList?.items ?? [],
    totalItems: filteredCandidateList?.totalItems ?? 0,
    isLoading: filteredCandidateListLoading,
    filterQueryString,
  };
}

export function useFilteredAddCandidatesToGroupPagingQuery(enabled = true, clientId?: string) {
  const key =
    clientId && clientId.length
      ? `add-to-group-${clientId}-${STORAGE_VERSION_DATE}`
      : `add-to-group-${STORAGE_VERSION_DATE}`;
  const { page, filterQueryString, page2FilterQueryString } = useCandidatesFilterStore(key);
  const { data: filteredCandidateList, isLoading: filteredCandidateListLoading } = useFilteredCandidatesQuery(
    filterQueryString,
    enabled,
  );
  const shouldFetchNextPage = filteredCandidateList && filteredCandidateList.totalPages > page + 1;
  useFilteredCandidatesQuery(page2FilterQueryString ?? "", shouldFetchNextPage);
  return {
    filteredCandidateList: filteredCandidateList?.items ?? [],
    totalItems: filteredCandidateList?.totalItems ?? 0,
    isLoading: filteredCandidateListLoading,
    filterQueryString,
  };
}

/**
 * Gets the Candidate's Change Tracking
 * @param candidateId The id of the candidate to see change tracking of
 * @returns UseQueryResult<ICandidateHistory[]>
 */
export function useCandidateHistoryQuery(candidateId: string) {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useQuery(
    ["candidateHistory", candidateId],
    async () => {
      const response = (await secureFetcher(
        apiPathCandidateProfile + "history/" + clientId + "/" + candidateId,
        token,
      )) as ICandidateHistory[];

      return response.map((history) => convertHistoryDates(history));
    },
    {
      enabled: !!token,
    },
  );
}

/**
 * Gets a candidate profile for the candidate with the given id
 * @param candidateId The id of the candidate to get the profile for
 * @returns UseQueryResult<ICandidateProfile>
 */
export function useCandidateProfileQuery(candidateId: string) {
  const token = useAuthToken();

  return useQuery<ICandidateProfile>(
    ["candidateProfile", candidateId],
    async () => {
      const response = (await secureFetcher(
        apiPathCandidateProfile + candidateId,
        token,
      )) as ICandidateProfile;
      return parseCandidate(response) as ICandidateProfile;
    },
    {
      enabled: !!token,
    },
  );
}

export function useCandidateProfileCampaignResponsesQuery(candidateId: string, query?: string) {
  const token = useAuthToken();

  return useQuery<IPagination<IFormQueryResponse>>(
    ["candidateProfileResponses", { candidateId, query }],
    () => secureFetcher(apiPathCandidateProfile + candidateId + "/responses" + query, token),
    {
      enabled: !!candidateId && !!token,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
}

/**
 * Converts dates from JSON string to Date objects
 *
 * @param candidate ICandidate with dates in string format
 * @returns ICandidate with date objects
 */
function convertCandidateDates(candidate: ICandidate | ICandidateProfile): ICandidate {
  return {
    ...candidate,
    createdDate: getDateUtc(candidate.createdDate),
    lastInteractedWithDate: getDateUtc(candidate.lastInteractedWithDate),
    lastUpdatedDate: getDateUtc(candidate.updatedDate),
  };
}

export function parseCandidate(candidate: ICandidate | ICandidateProfile): ICandidate {
  const workingCandidate = convertCandidateDates(candidate);
  return {
    ...workingCandidate,
    emailAddresses: workingCandidate.emailAddresses.filter((email) => email.emailAddress !== ""),
    phoneNumbers: workingCandidate.phoneNumbers.filter((phoneNumber) => phoneNumber.phoneNumber !== ""),
    groups: workingCandidate.groups
      ? workingCandidate.groups.sort(
          (a: IGroup | ICandidateProfileGroup, b: IGroup | ICandidateProfileGroup) =>
            a.name.localeCompare(b.name, "en", { sensitivity: "base" }),
        )
      : [],
    notes: workingCandidate.notes
      ? workingCandidate.notes.sort((a, b) => {
          // Sort by most recent first
          return new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime();
        })
      : [],
    fields:
      workingCandidate.fields?.map((field) => {
        const isAreaOfInterest = field.key.toLowerCase() === "area_of_interest";
        const value =
          isAreaOfInterest && typeof field.value === "string" && field.value.includes("|")
            ? field.value.split("|")
            : field.value;
        return {
          ...field,
          value,
        };
      }) ?? [],
  };
}

function convertHistoryDates(candidateHistory: ICandidateHistory): ICandidateHistory {
  return {
    ...candidateHistory,
    dateChanged: getDateUtc(candidateHistory.dateChanged),
    changedCandidate: convertCandidateDates(candidateHistory.changedCandidate),
  };
}

/**
 * Triggers the import of candidates from a CSV file
 *
 * @returns UseMutationResult<boolean, unknown, FormData, unknown>
 */
export const useEditCandidateProfileMutation = (optimistic = true) => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { id } = useCurrentUser();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<
    IEditCandidateProfileResponse,
    unknown,
    IEditCandidateProfile,
    unknown & { previousCandidateProfile?: ICandidateProfile }
  >(
    async (request) => {
      const command = {
        ...request,
        userId: id,
      } as IEditCandidateProfileRequest;
      return await fetcher(apiPathCandidates + "editcandidateprofile", {
        method: "PATCH",
        body: JSON.stringify(command),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onMutate: async (request) => {
        if (optimistic) {
          await queryClient.cancelQueries(["candidateProfile"]);
          const previousCandidateProfile = queryClient.getQueryData<ICandidateProfile>([
            "candidateProfile",
            request.candidateId,
          ]);

          const newOptimisticEmails = previousCandidateProfile?.emailAddresses?.map((email) => {
            if (email.id === request.emailAddress?.emailAddressId) {
              return {
                ...email,
                emailAddress: request.emailAddress?.emailAddress ?? email.emailAddress,
                emailAddressType: request.emailAddress?.emailType
                  ? convertStringToCandidateEmailType(request.emailAddress.emailType)
                  : email.emailAddressType,
              } as ICandidateEmailAddress;
            }
            return email;
          });

          const newOptimisticPhones = previousCandidateProfile?.phoneNumbers?.map((phone) => {
            if (phone.id === request.phoneNumber?.phoneNumberId) {
              const isValid =
                phone.phoneNumber === request.phoneNumber?.phoneNumberRaw ? phone.isValid : false;
              return {
                ...phone,
                phoneNumber: request.phoneNumber?.phoneNumberRaw ?? phone.phoneNumber,
                phoneType: request.phoneNumber?.phoneType
                  ? convertStringToCandidatePhoneNumberType(request.phoneNumber.phoneType)
                  : phone.phoneType,
                isValid,
              } as ICandidatePhoneNumber;
            }
            return phone;
          });

          const optimisticProfile = {
            ...previousCandidateProfile,
            phoneNumbers: newOptimisticPhones,
            emailAddresses: newOptimisticEmails,
          } as ICandidateProfile;

          queryClient.setQueryData<ICandidateProfile>(["candidateProfile", request.candidateId], (old) => ({
            ...(old ?? {}),
            ...optimisticProfile,
          }));

          return { previousCandidateProfile };
        }
      },
      onSettled: (response, error, request, context) => {
        const editError = Boolean(response?.success === false && response?.errorMessage);
        if (error || editError) {
          console.error("Error editing Candidate Profile", response?.errorMessage ?? error);
          if (optimistic && context?.previousCandidateProfile) {
            queryClient.setQueryData<ICandidateProfile>(
              ["candidateProfile", request.candidateId],
              context?.previousCandidateProfile,
            );
          }
        } else if (response) {
          if (optimistic) {
            queryClient.setQueryData<ICandidateProfile>(
              ["candidateProfile", request.candidateId],
              (oldProfile) => {
                if (!oldProfile) return oldProfile;

                const updatedEmails = request.newEmailAddress
                  ? [
                      ...(oldProfile.emailAddresses || []),
                      {
                        emailAddress: request.newEmailAddress.emailAddress,
                        emailAddressType: request.newEmailAddress.emailType
                          ? convertStringToCandidateEmailType(request.newEmailAddress.emailType)
                          : undefined,
                      } as ICandidateEmailAddress,
                    ]
                  : oldProfile.emailAddresses;

                // Ensure new phone number is added to the profile
                const updatedPhones = request.newPhoneNumber
                  ? [
                      ...(oldProfile.phoneNumbers || []),
                      {
                        phoneNumber: request.newPhoneNumber.phoneNumberRaw,
                        phoneType: request.newPhoneNumber.phoneType
                          ? convertStringToCandidatePhoneNumberType(request.newPhoneNumber.phoneType)
                          : undefined,
                        isValid: false,
                      } as ICandidatePhoneNumber,
                    ]
                  : oldProfile.phoneNumbers;

                return {
                  ...oldProfile,
                  phoneNumbers: updatedPhones,
                  emailAddresses: updatedEmails,
                };
              },
            );
          }

          queryClient.invalidateQueries(["candidateProfile", request.candidateId]);
          queryClient.invalidateQueries(["candidates", clientId]);
          queryClient.invalidateQueries(["groupMembers", clientId]);
          queryClient.invalidateQueries(["searchTags", clientId]);
        }
      },
      mutationKey: ["editCandidateProfile", id],
    },
  );
};

export const useCandidateAttachments = (candidateId: string) => {
  const token = useAuthToken();
  return useQuery<ICandidateAttachment[]>(
    ["candidateAttachments", candidateId],
    () => secureFetcher(apiPathCandidateProfile + candidateId + "/attachments", token),
    // () => ([] as ICandidateAttachment[]),
    {
      enabled: !!token && !!candidateId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

export const useUploadCandidateAttachmentMutation = () => {
  const user = useCurrentUser();
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<IUploadMediaDto, unknown, IUploadCandidateAttachment, ICandidateAttachment[]>(
    (upload) => {
      const path = `${apiPathCandidateProfile}${upload.candidateId}/attachments?userId=${user.id}`;
      return secureFetcher(path, token, {
        method: "POST",
        body: upload.file,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onMutate: async (upload) => {
        // Cancel any existing queries for candidate attachments
        await queryClient.cancelQueries(["candidateAttachments"]);
        // Get the current candidate attachments
        const backup = queryClient.getQueryData<ICandidateAttachment[]>([
          "candidateAttachments",
          upload.candidateId,
        ]);
        // Optimistically update the candidate attachments with uploaded file
        queryClient.setQueryData<ICandidateAttachment[]>(
          ["candidateAttachments", upload.candidateId],
          (prev) => [
            {
              id: "temp",
              name: upload.fileName,
              createdDate: dayjs(new Date().toISOString()),
              fileUrl: "",
              remoteId: "",
              userIdCreated: user.id,
              candidateId: upload.candidateId,
              isValid: false,
              uploadStatus: "",
            },
            ...(prev ?? []),
          ],
        );
        // Return the previous candidate attachments to be used in case of an error
        return backup;
      },
      onSettled: (uploadResponse, fetchError, upload, backup) => {
        if (uploadResponse?.hasError || fetchError) {
          console.error("Error on file upload", uploadResponse?.errorMessage || fetchError);
          if (backup) {
            queryClient.setQueryData<ICandidateAttachment[]>(
              ["candidateAttachments", upload.candidateId],
              backup,
            );
          }
        } else if (uploadResponse) {
          queryClient.invalidateQueries(["candidateAttachments"]);
          queryClient.invalidateQueries(["candidates", clientId]);
          queryClient.invalidateQueries(["groupMembers", clientId]);
          queryClient.invalidateQueries(["searchTags", clientId]);
        }
      },
    },
  );
};

export const useExportCandidateAttachments = (groupId = "") => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation(
    (request: ICandidateAttachmentsExportRequest) => {
      const path = groupId
        ? `${apiPathGroup}${clientId}/${groupId}/export/attachments`
        : `${apiPathCandidates}${clientId}/export/attachments`;
      return secureFetcher(path, token, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }) as Promise<SuccessResponse>;
    },
    {
      onError: (error) => {
        console.error("Error exporting attachments", error);
      },
      mutationKey: ["exportAttachments", clientId],
    },
  );
};

export const downloadFile = async (token: string, serverSideExportUrl: string, fileName = "export.csv") => {
  if (token) {
    const data: Blob = await secureFetcher(serverSideExportUrl, token, undefined, undefined, true);
    const url = window.URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

export const downloadFilePost = async (
  token: string,
  serverSideExportUrl: string,
  body: string | undefined,
  fileName = "export.csv",
) => {
  if (token) {
    const data: Blob = await secureFetcher(
      serverSideExportUrl,
      token,
      {
        body: body,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
      undefined,
      true,
    );
    const url = window.URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

/**
 * Export candidate data with Select All support
 */
export const exportCandidates = async (
  token: string,
  clientId: string,
  request: ICandidateExportRequest,
  fileName = "export.csv",
) => {
  if (token) {
    const data: Blob = await secureFetcher(
      `${apiPathCandidates}${clientId}/export`,
      token,
      {
        body: JSON.stringify(request),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
      undefined,
      true,
    );
    const url = window.URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

export const useCreateCandidate = () => {
  const user = useCurrentUser();
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<IAddCandidate, unknown, IAddCandidate>(
    (candidate) => {
      const path = apiPathCandidates + clientId;
      candidate.userId = user.id;
      return secureFetcher(path, token, {
        method: "POST",
        body: JSON.stringify(candidate),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["candidates", clientId]);
      },
    },
  );
};

export const useRollbackCandidate = () => {
  const user = useCurrentUser();
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<IRollbackCandidateRequest, unknown, IRollbackCandidateRequest>(
    (request) => {
      const path = apiPathCandidateProfile + `history/${request.candidateId}/rollback/${request.historyId}`;
      request.userId = user.id;
      return secureFetcher(path, token, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["candidates", clientId]);
        queryClient.invalidateQueries(["candidateProfile", data.candidateId]);
        queryClient.invalidateQueries(["candidateHistory", data.candidateId]);
      },
    },
  );
};

export const useBulkUpsertCandidateTags = () => {
  const user = useCurrentUser();
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<IBulkUpsertCandidateTags, unknown, IBulkUpsertCandidateTags>(
    (tagRequest) => {
      const path = `${apiPathCandidates}tags/upsert`;
      tagRequest.userId = user.id;
      tagRequest.clientId = clientId;

      return secureFetcher(path, token, {
        method: "POST",
        body: JSON.stringify(tagRequest),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["candidates", clientId]);
        queryClient.invalidateQueries(["groupMembers", clientId]);
        queryClient.invalidateQueries(["searchTags", clientId]);
      },
    },
  );
};

export const useDeleteCandidates = () => {
  const user = useCurrentUser();
  const token = useAuthToken();
  const queryClient = useQueryClient();

  return useMutation<IDeleteCandidatesResult, unknown, IDeleteCandidatesRequest>(
    (request) => {
      const candidateIds = request.candidateIds.join(",");
      const path = `${apiPathCandidates}${request.clientId}/${user.id}/candidates?candidateIds=${candidateIds}`;
      return secureFetcher(path, token, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    {
      onSuccess: (_, request) => {
        queryClient.invalidateQueries(["candidates", request.clientId]);
      },
    },
  );
};

/**
 * Adds candidate to groups. If they already are added, it will not add them.
 * @returns
 */
export const useCandidateToGroupsMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation<ISuccessResponse, unknown, IAddCandidateToGroupsCommand>(
    (command) => {
      const path = `${apiPathCandidateProfile}client/${clientId}/${command.candidateId}/groups`;
      return fetcher(path, {
        method: "POST",
        body: JSON.stringify(command.groupIds),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["candidateProfile"]),
      mutationKey: ["addCandidateToGroups"],
    },
  );
};

/**
 * Gets list of groups for the currently selected client
 *
 * @returns UseQueryResult<ICandidate[]>
 */
export const useCandidateSearchQuery = (data: string, pagination: IPaginationRequest) => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  let query = `?page=${pagination.page + 1}&pageSize=${pagination.pageSize}`;

  if (pagination.search) {
    query += `&search=${pagination.search}`;
  }

  if (pagination.sortBy) {
    query += `&sortBy=${pagination.sortBy}`;
  }

  if (pagination.sortDirection) {
    query += `&sortDirection=${pagination.sortDirection}`;
  }

  return useQuery<IPagination<ICandidate>>(
    ["candidateSearchQuery", clientId, JSON.stringify(pagination), data],
    async () => {
      const response = (await secureFetcher(`${apiPathCandidates}${clientId}/search${query}`, token, {
        body: JSON.stringify({ rules: data }),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      })) as IPagination<ICandidate>;
      response.items = response.items.map((candidate: ICandidate) => parseCandidate(candidate));
      return response;
    },
    {
      enabled: !!clientId && !!token && !!data,
    },
  );
};
